import { IconCollection } from "components/Icon/IconCollection";
import {
    ControlIcon,
    IconWithTooltip,
} from "components/ObjectTemplateCanvas/components/components";
import RecordLabel from "components/RecordLabel";
import { ResourceConfig } from "config/config";
import {
    DeleteListAction,
    PermanentlyDeleteListAction,
    deleteActionSignature,
    deletePermanentlyActionSignature,
    duplicateActionSignature,
    exportActionSignature,
    moveToLocationActionSignature,
} from "containers/Resources/components/CommonActions/CommonActions";
import {
    commentsTab,
    logsTab,
    logsTabKey,
    metadataTab,
} from "containers/Resources/screens/ResourcesSingle/constants";
import { Object_Template } from "graphql/generated/graphql";
import { getRelativeUrl } from "utils/service";
import resource from "./resource";

const FlowRecord = ({ data }: { data: any }) => {
    if (data) {
        const { name, id } = data;
        return <RecordLabel name={name} link={getRelativeUrl("flow", id)} />;
    }
    return null;
};

const getInitialAutocompleteFlowOption = (value: any) =>
    value
        ? {
              value: value.id,
              label: value.name,
              link: getRelativeUrl("flow", value.id),
          }
        : null;

const commonResourceBody = `
${resource.graphql.resourceBody}
before_create
before_create_flow{
    id
    name
}
on_create
on_create_flow{
    id
    name
}
on_update
on_update_flow{
    id
    name
}
on_delete
on_delete_flow{
    id
    name
}
icon
`;

const resourceName = "object_template";

const config: ResourceConfig = {
    label: "Object template",
    icon: IconCollection.objectTemplate,
    resourceName,
    resourceClass: "resource",
    defaultActiveTab: logsTabKey,
    graphql: {
        resource: resourceName,
        resourceBody: `
            ${commonResourceBody}
            object_template_attribute_list_object_template_id(limit:10,order:"+sort_order,+name",offset:0){
                object_template_attribute_count
                object_template_attribute_list {
                    id
                    name
                    object_template_id
                    datatype
                    reference_object_template_id
                    reference_record_type
                    is_nullable
                    is_unique
                    is_hidden
                    silent_update
                    searchable
                    sort_order
                }
            }
            custom_object_list_object_template_id(limit:10,order:"+name",offset:0){
                custom_object_count
                custom_object_list {
                    id
                    name
                    object_template_id
                    provisioning_state
                    value
                    description
                    is_readonly
                    is_project_readonly
                    is_bundle_readonly
                    deleted_at
                    project_id
                    bundle_id
                }
            }
            object_template_attribute_plain_list_reference_object_template_id(limit:0,order:"+name",offset:0){
                id        
                object_template_id
            }
        `,
        resourceSingleQueryBody: `
            ${commonResourceBody}
            object_template_attribute_list_object_template_id(limit:$attributeLimit,order:$attributeOrder,offset:$attributeOffset){
                object_template_attribute_count
                object_template_attribute_list {
                    id
                    name
                    object_template_id
                    datatype
                    reference_object_template_id
                    reference_record_type
                    is_nullable
                    is_unique
                    is_hidden
                    silent_update
                    searchable
                    sort_order
                }
            }
            custom_object_list_object_template_id(limit:$customObjectLimit,order:$customObjectOrder,offset:$customObjectOffset,allow_deleted:$customObjectAllowDeleted,allow_normal:$customObjectAllowNormal){
                custom_object_count
                custom_object_list {
                    id
                    name
                    object_template_id
                    provisioning_state
                    value
                    description
                    is_readonly
                    is_project_readonly
                    is_bundle_readonly
                    deleted_at
                    project_id
                    bundle_id
                    referencing_custom_objects
                    created_at
                    created_by_identity{
                        identity_type
                        id
                        name
                        is_deleted
                    }
                    modified_at
                    modified_by_identity{
                        identity_type
                        id
                        name
                        is_deleted
                    }
                    project_id_project{
                        id
                        name
                        icon
                    }
                    bundle_id_bundle{
                        id
                        name
                        icon
                    }
                    workspace_id_workspace{
                        id
                        name
                    }
                }
            }
            object_template_attribute_plain_list_reference_object_template_id(limit:$referenceLimit,order:"+name",offset:0){
                id        
                object_template_id
            }
        `,
        resourceBodyListing: `
            ${resource.graphql.resourceBody}
            custom_object_list_object_template_id (limit: 0, offset: 0, order: "+name") {
                custom_object_count
            }
            before_create_flow{
                id
                name
            }
            on_create_flow{
                id
                name
            }
            on_update_flow{
                id
                name
            }
            on_delete_flow{
                id
                name
            }
        `,
        createVariables: `
            ${resource.graphql.createVariables(resourceName)}
            $before_create: String,
            $on_create: String,
            $on_update: String,
            $on_delete: String,
            $icon: String,
        `,
        createQuery: `
            ${resource.graphql.createQuery(resourceName)}
            before_create: $before_create,
            on_create: $on_create,
            on_update: $on_update,
            on_delete: $on_delete,
            icon: $icon,
        `,
        updateVariables: `
            ${resource.graphql.updateVariables(resourceName)}
            $before_create: String,
            $on_create: String,
            $on_update: String,
            $on_delete: String,
            $icon: String,
        `,
        updateQuery: `
            ${resource.graphql.updateQuery(resourceName)}
            before_create: $before_create,
            on_create: $on_create,
            on_update: $on_update,
            on_delete: $on_delete,
            icon: $icon,
        `,
        singleQueryVariables: `
            $customObjectLimit: Int = 10,
            $customObjectOffset: Int = 0,
            $customObjectOrder: String = "+name",
            $customObjectAllowDeleted: Boolean = false,
            $customObjectAllowNormal: Boolean = true,
            $attributeLimit: Int = 10,
            $attributeOffset: Int = 0,
            $attributeOrder: String = "+sort_order,+name",
            $referenceLimit: Int = 0,
        `,
    },
    additionalColumns: () => [
        {
            label: "Before create",
            key: "before_create_flow",
            shown: true,
            sortable: false,
            dto: (value: Object_Template) => <FlowRecord data={value?.before_create_flow} />,
        },
        {
            label: "On create",
            key: "on_create_flow",
            shown: true,
            sortable: false,
            dto: (value: Object_Template) => <FlowRecord data={value?.on_create_flow} />,
        },
        {
            label: "On update",
            key: "on_update_flow",
            shown: true,
            sortable: false,
            dto: (value: Object_Template) => <FlowRecord data={value?.on_update_flow} />,
        },
        {
            label: "On delete",
            key: "on_delete_flow",
            shown: true,
            sortable: false,
            dto: (value: Object_Template) => <FlowRecord data={value?.on_delete_flow} />,
        },
        {
            label: "Number of custom objects",
            key: "number_of_custom_objects",
            shown: true,
            sortable: false,
            dto: (value: Object_Template) =>
                value?.custom_object_list_object_template_id?.custom_object_count ?? 0,
        },
    ],
    layout: [
        { partial: "pageHeaderForm" },
        { partial: "form" },
        { partial: "objectTemplateAttributeList" },
        { partial: "customObjectList" },
        {
            partial: "tabs",
            config: {
                tabs: [logsTab.tab, metadataTab.tab, commentsTab.tab],
                content: [logsTab.content, metadataTab.content, commentsTab.content],
            },
        },
    ],
    actions: {
        shown: [],
        hidden: [
            duplicateActionSignature,
            exportActionSignature,
            moveToLocationActionSignature,
            deleteActionSignature,
            deletePermanentlyActionSignature,
        ],
    },
    listActions: {
        shown: [],
        hidden: [
            { key: "delete", action: DeleteListAction },
            { key: "deletePermanently", action: PermanentlyDeleteListAction },
        ],
    },
    form: [
        {
            name: "before_create",
            component: "autocomplete",
            fieldProps: {
                label: "Before create",
                options: {
                    resource: "flow",
                },
                getInitialAutocompleteOption: (value) =>
                    getInitialAutocompleteFlowOption(value?.before_create_flow),
                docs: {
                    text: "Choose a flow to call before a custom object of object template is created. This flow can set default values for the custom object.",
                },
                iconOnlyComponent: ({ hasValue, isDisabled }) => (
                    <IconWithTooltip
                        inner={
                            <ControlIcon
                                icon={IconCollection.plusCircleOutline}
                                hasValue={hasValue}
                                iconColor="primary-green"
                            />
                        }
                        overlay={`flow before create${hasValue ? `: ${hasValue}` : ""}`}
                        isDisabled={isDisabled}
                        align={{ offset: [15, 5] }}
                    />
                ),
            },
        },
        {
            name: "on_create",
            component: "autocomplete",
            fieldProps: {
                label: "On create",
                options: {
                    resource: "flow",
                },
                docs: {
                    text: "Choose a flow to call when a custom object of object template is created",
                },
                getInitialAutocompleteOption: (value) =>
                    getInitialAutocompleteFlowOption(value?.on_create_flow),
                iconOnlyComponent: ({ hasValue, isDisabled }) => (
                    <IconWithTooltip
                        inner={
                            <ControlIcon
                                icon={IconCollection.plus}
                                hasValue={hasValue}
                                iconColor="primary-green"
                            />
                        }
                        overlay={`flow on create${hasValue ? `: ${hasValue}` : ""}`}
                        isDisabled={isDisabled}
                        align={{ offset: [15, 5] }}
                    />
                ),
            },
        },
        {
            name: "on_update",
            component: "autocomplete",
            fieldProps: {
                label: "On update",
                options: {
                    resource: "flow",
                },
                getInitialAutocompleteOption: (value) =>
                    getInitialAutocompleteFlowOption(value?.on_update_flow),
                docs: {
                    text: "Choose a flow to call when a custom object of the object template is updated",
                },
                iconOnlyComponent: ({ hasValue, isDisabled }) => (
                    <IconWithTooltip
                        inner={
                            <ControlIcon
                                icon={IconCollection.refresh}
                                hasValue={hasValue}
                                iconColor="primary-yellow"
                            />
                        }
                        overlay={`flow on update${hasValue ? `: ${hasValue}` : ""}`}
                        isDisabled={isDisabled}
                        align={{ offset: [15, 5] }}
                    />
                ),
            },
        },
        {
            name: "on_delete",
            component: "autocomplete",
            fieldProps: {
                label: "On delete",
                options: {
                    resource: "flow",
                },
                docs: {
                    text: "Choose a flow to call when a custom object of the object template is deleted",
                },
                getInitialAutocompleteOption: (value) =>
                    getInitialAutocompleteFlowOption(value?.on_delete_flow),
                iconOnlyComponent: ({ hasValue, isDisabled }) => (
                    <IconWithTooltip
                        inner={
                            <ControlIcon
                                icon={IconCollection.close}
                                hasValue={hasValue}
                                iconColor="primary-red"
                            />
                        }
                        overlay={`flow on delete${hasValue ? `: ${hasValue}` : ""}`}
                        isDisabled={isDisabled}
                        align={{ offset: [15, 5] }}
                    />
                ),
            },
        },
        {
            name: "icon",
            component: "imageUpload",
            fieldProps: {
                label: "Icon for custom objects",
            },
        },
    ],
};
export default config;
